import React, { useMemo } from "react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../../select";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAgencyAccounts } from "@/hooks/useAgencyAccounts";
import { LeftNavigationData, SettingsNavigationData } from "@/data/LeftNavigation";
import { usePermissions } from "@/hooks/usePermissions";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as Icons from "../Icons";
import { APP_VERSION } from "@/config/version";

interface LeftNavigationProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const LeftNavigation: React.FC<LeftNavigationProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { accounts, currentAccount, isAgencyUser, hasMultipleAccounts, switchAccount, loading: accountsLoading } = useAgencyAccounts();
  const { hasPermission } = usePermissions();

  const handleLogout = async () => {
    console.log('🚪 [LeftNavigation] Iniciando logout...')
    console.log('🗑️ [LeftNavigation] Limpando sessão do localStorage...')
    await logout();
    // O redirecionamento será feito automaticamente pelo ProtectedRoute
  };

  const handleAccountSwitch = async (accountId: string) => {
    console.log('🔄 [LeftNavigation] Trocando conta:', accountId)
    const success = await switchAccount(accountId)
    if (!success) {
      console.error('❌ [LeftNavigation] Falha ao trocar conta')
    }
  };

  // Exibir select para agency_users OU account_users com múltiplas contas
  const shouldShowAccountSelect = (
    (isAgencyUser && accounts.length > 0) ||
    (hasMultipleAccounts && accounts.length > 1)
  );

  console.log('[LeftNavigation] Account select visibility:', {
    isAgencyUser,
    hasMultipleAccounts,
    userEmail: user?.email,
    userType: user?.type,
    accountsCount: accounts.length,
    shouldShow: shouldShowAccountSelect,
    note: 'Select shows for agency_users OR account_users with multiple accounts'
  });

  const filteredMainNav = useMemo(() => {
    const filtered = LeftNavigationData.filter(item => {
      if (!item.permission) return true;
      const hasAccess = hasPermission(item.permission);
      return hasAccess;
    });

    console.log('[LeftNavigation] Main nav filtered:', {
      total_items: LeftNavigationData.length,
      filtered_items: filtered.length,
      user_email: user?.email,
      user_type: user?.type,
      items: filtered.map(i => i.title)
    });

    return filtered;
  }, [hasPermission, user?.email, user?.type]);

  const filteredSettingsNav = useMemo(() => {
    const filtered = SettingsNavigationData.filter(item => {
      if (item.title === "Log out") return true;
      if (!item.permission) return true;
      const hasAccess = hasPermission(item.permission);
      return hasAccess;
    });

    console.log('[LeftNavigation] Settings nav filtered:', {
      total_items: SettingsNavigationData.length,
      filtered_items: filtered.length,
      items: filtered.map(i => i.title)
    });

    return filtered;
  }, [hasPermission, user?.email, user?.type]);

  return (
    <div className="w-full overflow-hidden  h-[93dvh]  border-r border-neutral-800 relative group">
      <button
        onClick={onToggle}
        className={clsx(
          "absolute top-[26px] right-[6px] z-50 w-7 h-7 bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 rounded-full flex items-center justify-center transition-all duration-200",
          "opacity-0 group-hover:opacity-100",
          isCollapsed && "opacity-100",
          isCollapsed && "right-[20px]"
        )}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-neutral-300" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-neutral-300" />
        )}
      </button>

      <div className={clsx("transition-all duration-300 ease-in-out px-4 py-4 h-full flex flex-col", isCollapsed ? "px-2" : "px-4")}>
        <div className="flex flex-col gap-3 flex-1">
          {shouldShowAccountSelect && (
            <div
              className={clsx("w-full flex flex-col gap-3 transition-all duration-300", isCollapsed ? "hidden" : "block")}
            >
              <Select
                value={currentAccount?.id}
                onValueChange={handleAccountSwitch}
                disabled={accountsLoading}
              >
                <SelectTrigger className={clsx("transition-all duration-300", isCollapsed ? "w-[50px]" : "w-full")}>
                  <SelectValue placeholder="Selecionar Conta">
                    {accountsLoading ? "Carregando..." : currentAccount?.nome || "Selecionar Conta"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.nome}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* for small screen ---->  */}
          <button
            className={clsx(
              "w-[48px] h-[48px] rounded-[12px] flex justify-center border border-[#3F3F464D] items-center",
              isCollapsed ? "block" : "hidden"
            )}
          >
            <Icons.unfold className="w-[16px] h-[16px]" />
          </button>

          {/* Main Navigation */}
          <div className="w-full flex flex-col gap-2">
            {filteredMainNav.map((item, index) => {
              const isActive = item.path === location.pathname;

              return (
                <div key={index} className="relative w-full">
                  <Link
                    to={item.path}
                    className={clsx(
                      "flex w-full gap-3 px-3 min-h-[48px] rounded-[12px] transition-all border border-transparent duration-200 relative overflow-hidden group/item",
                      isActive
                        ? "bg-gradient-to-r border !border-red-500/30 from-red-900/20 to-red-600/20  text-red-200"
                        : "hover:bg-neutral-800/50 text-neutral-300 hover:text-neutral-100",
                      isCollapsed ? "justify-center items-center !w-[48px]" : "items-center justify-start"
                    )}
                  >
                    {/* border border-red-500/30 */}
                    <div className={clsx("flex-shrink-0 transition-all duration-200 rounded-lg")}>{item.icon}</div>

                    {/* Text */}
                    <p
                      className={clsx(
                        "text-[14px] font-medium transition-all duration-300 whitespace-nowrap",
                        isCollapsed ? "opacity-0 hidden scale-95 w-0" : "block"
                      )}
                    >
                      {item.title}
                    </p>
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Settings Navigation */}
          <div className="w-full flex flex-col gap-3 -mt-3">
            <div className={clsx("    transition-all duration-300", isCollapsed ? "mx-2" : "mx-0")} />

            {filteredSettingsNav.map((item, index) => {
              const isActive = item.path === location.pathname;

              return (
                <div key={index} className="relative">
                  {item.title === "Log out" ? (
                    <button
                      onClick={handleLogout}
                      className={clsx(
                        "flex w-full gap-3 px-3 min-h-[48px] rounded-xl transition-all border border-transparent duration-200 relative overflow-hidden group/item",
                        "hover:bg-neutral-800/50 text-neutral-300 hover:text-neutral-100",
                        isCollapsed ? "justify-center items-center !w-[48px]" : "items-center justify-start"
                      )}
                    >
                      <div className={clsx("flex-shrink-0 transition-all duration-200 rounded-lg")}>{item.icon}</div>
                      <p
                        className={clsx(
                          "text-[14px] font-medium transition-all duration-300 whitespace-nowrap",
                          isCollapsed ? "opacity-0 hidden scale-95 w-0" : "block"
                        )}
                      >
                        {item.title}
                      </p>
                    </button>
                  ) : (
                    <Link
                      to={item.path}
                      className={clsx(
                        "flex w-full gap-3 px-3 min-h-[48px] rounded-xl transition-all border border-transparent duration-200 relative overflow-hidden group/item",
                        isActive
                          ? "bg-gradient-to-r border !border-red-500/30 from-red-900/20 to-red-600/20  text-red-200"
                          : "hover:bg-neutral-800/50 text-neutral-300 hover:text-neutral-100",
                        isCollapsed ? "justify-center items-center !w-[48px]" : "items-center justify-start"
                      )}
                    >
                      <div className={clsx("flex-shrink-0 transition-all duration-200 rounded-lg")}>{item.icon}</div>
                      <p
                        className={clsx(
                          "text-[14px] font-medium transition-all duration-300 whitespace-nowrap",
                          isCollapsed ? "opacity-0 hidden scale-95 w-0" : "block"
                        )}
                      >
                        {item.title}
                      </p>
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className={clsx(
          "text-neutral-500 text-xs py-2 transition-all duration-300",
          isCollapsed ? "text-center" : "text-left"
        )}>
          {isCollapsed ? `v${APP_VERSION.split('.')[0]}` : `v${APP_VERSION}`}
        </div>
      </div>
    </div>
  );
};

export default LeftNavigation;
